import { type JSX, useState } from "react"
import { useParams } from "react-router"
import styled from "styled-components"

import { type UserCoursePageInput, type PersonalizedCourse } from "@forento/shared/models/course"
import { parseNumber } from "@forento/shared/utilities/number"

import { useCourseDetail } from "~/api/course"
import { type PaypalOrder, PurchaseSuccessModalContextProvider } from "~/contexts/PurchaseSuccessModalContext"
import { useUser } from "~/contexts/UserContext"
import Button from "~/themes/original/components/Button"
import CourseComments from "~/themes/original/components/CourseComments"
import FullLoadingPage from "~/themes/original/components/FullLoadingPage"
import Hero, { CallToActionIcon, CallToActionInfo } from "~/themes/original/components/Hero"
import { ArrowIcon } from "~/themes/original/components/Icon"
import Layout from "~/themes/original/components/Layout"
import PurchaseItem from "~/themes/original/components/PurchaseItem"
import { ControlledTabs } from "~/themes/original/components/Tabs"
import NotFoundPage from "~/themes/original/pages/NotFoundPage"
import { toRelativeDateString } from "~/themes/original/utilities/date"
import { useTranslation } from "~/translations"
import routes from "~/utilities/routes"

import ChaptersList from "./ChaptersList"
import Inputs from "./Inputs"
import Resources from "./Resources"

const CourseDetailPage: React.FC = () => {
	const courseId = parseNumber(useParams().courseId!)
	const [data, reload] = useCourseDetail(courseId ?? -1)
	const user = useUser()
	const t = useTranslation()

	const [paypalOrder, setPaypalOrder] = useState<PaypalOrder | null>(null)

	switch (data.status) {
		case "error":
			return <Layout>Failed to load course.</Layout>
		case "loading":
			return <FullLoadingPage />
		case "not-found":
			return <NotFoundPage />
		case "success": {
			const course = data.course
			const inputs = data.inputs

			const callToAction: JSX.Element = (() => {
				switch (data.button.variant) {
					case "start":
						return (
							<>
								{data.button.expireDate !== null && (
									<CallToActionInfo>
										{t("course.accessExpires", {
											date: toRelativeDateString(data.button.expireDate),
										})}
									</CallToActionInfo>
								)}
								<Button variant="primary" onClick={routes.course.player(course.id)}>
									<CallToActionIcon as={ArrowIcon} />
									Start
								</Button>
							</>
						)
					case "continue":
						return (
							<>
								{data.button.expireDate !== null && (
									<CallToActionInfo>
										{t("course.accessExpires", {
											date: toRelativeDateString(data.button.expireDate),
										})}
									</CallToActionInfo>
								)}
								<Button variant="primary" onClick={routes.course.player(course.id)}>
									<CallToActionIcon as={ArrowIcon} />
									Continue
								</Button>
							</>
						)
					case "purchase":
						return (
							<PurchaseItem
								item={{
									type: "course",
									id: course.id,
									name: course.title,
									shortDescription: course.shortDescription,
									price: data.button.price,
									accessTimeMonths: data.button.accessTimeMonths,
								}}
								onPurchase={async ({ paypalOrder }) => {
									setPaypalOrder(paypalOrder)
									await reload()
								}}
							/>
						)
					case "membership":
						return (
							<>
								<CallToActionInfo>This course is only available through a membership.</CallToActionInfo>
								<Button variant="primary" onClick={routes.membership.index()}>
									Membership
								</Button>
							</>
						)
					case "bokadero-membership":
						return (
							<>
								<CallToActionInfo>Gratis för medlemmar i Bokadero Arena</CallToActionInfo>
								{user.user ? (
									<Button variant="primary" onClick={routes.user.settings()}>
										Hantera inställningar
									</Button>
								) : (
									<Button variant="primary" onClick={routes.user.signup()}>
										Skapa konto
									</Button>
								)}
							</>
						)
					case "paused":
						return <CallToActionInfo>{t("product.paused")}</CallToActionInfo>
				}
			})()

			return (
				<PurchaseSuccessModalContextProvider
					content={{ type: "course", id: course.id }}
					paypalOrder={paypalOrder}
				>
					<Layout
						metadata={{
							title: data.title,
							description: course.shortDescription,
							imageUrl: course.thumbnailFilePath ?? undefined,
						}}
					>
						<Hero
							title={course.title}
							thumbnailUrl={course.thumbnailFilePath}
							htmlDescription={course.shortDescription}
							tags={course.tags}
							callToAction={callToAction}
						/>
						<StyledTabs tabs={getTabs(course, inputs, reload)} />
					</Layout>
				</PurchaseSuccessModalContextProvider>
			)
		}
	}
}

function getTabs(course: PersonalizedCourse, inputs: UserCoursePageInput[], reloadCourse: () => Promise<unknown>) {
	const tabs = []
	if (course.presentationHtml !== null) {
		tabs.push({
			title: "Overview",
			content: (
				<TabContent>
					<div className="editorjs" dangerouslySetInnerHTML={{ __html: course.presentationHtml }} />
				</TabContent>
			),
		})
	}
	tabs.push({
		title: "Content",
		content: (
			<TabContent>
				<ChaptersList chapters={course.chapters} />
			</TabContent>
		),
	})
	if (course.status === "unlocked" && course.resources.length > 0) {
		tabs.push({
			title: "Resources",
			content: (
				<TabContent>
					<Resources resources={course.resources} />
				</TabContent>
			),
		})
	}
	if (course.commentsEnabled) {
		tabs.push({
			title: "Comments",
			content: (
				<TabContent>
					<CourseComments
						courseId={course.id}
						commentsUnderReview={course.status === "unlocked" ? course.commentsUnderReview : 0}
						pageId={null}
						reloadCourse={reloadCourse}
					/>
				</TabContent>
			),
		})
	}
	if (inputs.length > 0) {
		tabs.push({
			title: "Inputs",
			content: (
				<TabContent>
					<Inputs inputs={inputs} />
				</TabContent>
			),
		})
	}
	return tabs
}

const StyledTabs = styled(ControlledTabs)`
	@media (max-width: 999px) {
		margin: 0 16px;
	}
`

const TabContent = styled.div`
	margin-top: 32px;
`

export default CourseDetailPage
